import { CJEvent } from '@teetsh/app/src/types/event';
import { Classroom } from '@teetsh/app/src/types/classroom';
import _groupBy from 'lodash-es/groupBy';
import {
  BILAN_EVENT,
  BREAK_EVENT,
  CLASSIC_EVENT,
  LOADING_EVENT,
  RAPPEL_EVENT,
  RITUEL_EVENT,
} from '@teetsh/app/src/containers/constants';
import { format } from '@teetsh/app/src/utils/time/format';
import { doesDayBelongToWorkingDays } from './doesDayBelongToWorkingDays';
import { getGroupedEvents } from '@teetsh/app/src/containers/CahierJournal/utils/getGroupedEvents';
import { computeAddEvents } from './computeAddEvents';
import {
  ClassroomWithGroupeEvent,
  DayWithClassrooms,
} from '@teetsh/app/src/types/cj';
import { ClassroomsOrg } from '@teetsh/app/src/types/school';
import {
  getMutualTags,
  getMutualWorkingDays,
} from '@teetsh/app/src/containers/utils';
import { getSeancesByClassroomIds } from './getSeancesByClassroomIds';
import _some from 'lodash-es/some';

export function getDayOfCahierJournal(props: {
  seances: CJEvent[];
  classrooms: Classroom[];
  day: Date;
  classroomsOrg: ClassroomsOrg;
}): DayWithClassrooms {
  const { day, classroomsOrg } = props;
  const classrooms = props.classrooms.filter(cl =>
    doesDayBelongToWorkingDays(day, cl.workingDays),
  );
  const seances = (props.seances || []).filter(ev =>
    _some(ev.classroomIds, clId => classrooms.map(d => d.id).includes(clId)),
  );
  // We add classroomOrder to be able to sort by order then classroomOrder then createdAt in getGroupedEvents
  const seancesWithClassroomOrder = seances.map(ev => ({
    ...ev,
    classroomOrder: classrooms.find(cl => ev.classroomIds.includes(cl.id))
      ?.order,
  }));
  const dayClassrooms: Classroom[] =
    classroomsOrg === 'unified' && classrooms.length > 0
      ? [
          {
            cycle: [],
            events: [],
            id: classrooms.map(cl => cl.id).join('%%'),
            name: classrooms.map(cl => cl.name).join('-'),
            order: 0,
            schoolId: classrooms[0].schoolId,
            tags: getMutualTags(classrooms),
            workingDays: getMutualWorkingDays(classrooms),
          },
        ]
      : classrooms;

  const date = format(day, 'fr-FR');
  if (seancesWithClassroomOrder.length === 0) {
    return {
      day: date,
      classrooms: dayClassrooms.map(cl => ({ ...cl, groupedEvents: [] })),
    };
  }
  const eventsByTypes = _groupBy(seancesWithClassroomOrder, e => e.type);
  const eventsOfDay = [
    ...(eventsByTypes[CLASSIC_EVENT] || []),
    ...(eventsByTypes[LOADING_EVENT] || []),
    ...(eventsByTypes[BREAK_EVENT] || []),
    ...(eventsByTypes[RITUEL_EVENT] || []),
  ];
  const sortedByClassroom =
    classroomsOrg === 'split'
      ? getSeancesByClassroomIds(eventsOfDay)
      : {
          [classrooms.map(c => c.id).join('%%')]: eventsOfDay,
        };

  const sortedByClassroomWithAddEvents = Object.keys(sortedByClassroom).reduce(
    (acc, num) => {
      const eventsToDestructure = sortedByClassroom[num];
      acc[num] = [
        ...eventsToDestructure,
        ...computeAddEvents(eventsToDestructure, [num]),
      ];
      return acc;
    },
    {},
  );
  const cl: ClassroomWithGroupeEvent[] = dayClassrooms.map(classroom => {
    let groupedEvents: CJEvent[][][] =
      sortedByClassroomWithAddEvents[classroom.id] !== undefined
        ? getGroupedEvents(sortedByClassroomWithAddEvents[classroom.id])
        : [];

    const rappelEvents = (eventsByTypes[RAPPEL_EVENT] || []).filter(e =>
      _some(e.classroomIds, clId => classroom.id.split('%%').includes(clId)),
    );
    const bilanEvents = (eventsByTypes[BILAN_EVENT] || []).filter(e =>
      _some(e.classroomIds, clId => classroom.id.split('%%').includes(clId)),
    );
    if (rappelEvents.length > 0) {
      groupedEvents = [[rappelEvents], ...groupedEvents];
    }
    if (bilanEvents.length > 0) {
      groupedEvents = [...groupedEvents, [bilanEvents]];
    }

    return {
      ...classroom,
      groupedEvents,
    };
  });
  return {
    day: date,
    classrooms: cl,
  };
}
